import React, { useState } from 'react';
import LoginScreen from './LoginScreen';
import SignupScreen from './SignupScreen';
import LanguageSelector from '../LanguageSelector';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../../lib/supabaseClient';
import { signInWithOAuthNative } from '../../lib/nativeOAuth';
import { useLanguageContext } from '../../contexts/LanguageContext';

type AuthScreen = 'welcome' | 'login' | 'signup';

interface WelcomeScreenProps {
  onContinueAsGuest?: () => void;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onContinueAsGuest }) => {
  const [screen, setScreen] = useState<AuthScreen>('welcome');
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const isNative = Capacitor.isNativePlatform();
  const { language } = useLanguageContext();

  const handleOAuthLogin = async (provider: 'google' | 'apple') => {
    setOauthLoading(true);
    setOauthError(null);
    try {
      if (isNative) {
        // Flow natif : in-app browser + deep link retour
        const { error } = await signInWithOAuthNative(provider, language);
        if (error) throw error;
      } else {
        // Flow web : redirection classique
        const { error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: `${window.location.origin}?lang=${language}`,
            queryParams: provider === 'google' ? {
              access_type: 'offline',
              prompt: 'consent',
            } : undefined,
          },
        });
        if (error) throw error;
      }
    } catch (err: any) {
      console.error(`❌ Erreur ${provider} OAuth:`, err);
      setOauthError(err.message || `Erreur de connexion ${provider}`);
      setOauthLoading(false);
    }
  };

  if (screen === 'login') {
    return <LoginScreen onBack={() => setScreen('welcome')} />;
  }

  if (screen === 'signup') {
    return <SignupScreen onBack={() => setScreen('welcome')} />;
  }

  return (
    <div className="h-screen-adaptive bg-gradient-to-b from-purple-100 to-pink-100 flex items-center justify-center relative p-3 safe-area-top safe-area-bottom">
      {/* Language Selector - Top Right */}
      <div className={`absolute right-4 z-10 ${isNative ? 'top-0 safe-area-top pt-2' : 'top-4'}`}>
        <LanguageSelector />
      </div>

      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-5">
        {/* Logo */}
        <div className="text-center mb-4">
          <img
            src="/logo.png"
            alt="LilHero"
            className="mx-auto h-16 mb-2"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>

        <h1 className="text-2xl font-bold text-center mb-1 font-title">
          Bienvenue dans LilHero
        </h1>
        <p className="text-gray-600 text-center mb-4 text-sm">
          Des aventures interactives pour les enfants
        </p>

        {/* Benefits of creating an account */}
        <div className="bg-purple-50 rounded-lg border border-purple-200 p-3 mb-4">
          <ul className="text-purple-800 text-xs space-y-0.5">
            <li>✓ 1 nouvelle histoire par jour</li>
            <li>✓ Collectez des objets en lisant</li>
            <li>✓ Personnalisez votre monde</li>
            <li>✓ Sauvegardez votre progression</li>
            <li>✓ Synchronisé sur tous vos appareils</li>
          </ul>
        </div>

        <div className="space-y-2">
          {/* Google OAuth Button */}
          <button
            onClick={() => handleOAuthLogin('google')}
            disabled={oauthLoading}
            className="w-full bg-white text-gray-700 py-2.5 text-sm rounded-lg font-medium hover:bg-gray-50 transition-colors shadow-md border border-gray-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {oauthLoading ? 'Connexion...' : 'Continuer avec Google'}
          </button>

          {/* Apple OAuth Button */}
          <button
            onClick={() => handleOAuthLogin('apple')}
            disabled={oauthLoading}
            className="w-full bg-black text-white py-2.5 text-sm rounded-lg font-medium hover:bg-gray-900 transition-colors shadow-md flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            {oauthLoading ? 'Connexion...' : 'Continuer avec Apple'}
          </button>

          {oauthError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {oauthError}
            </div>
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">ou par email</span>
            </div>
          </div>

          <button
            onClick={() => setScreen('signup')}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2.5 text-sm rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all shadow-md"
          >
            Créer un compte
          </button>

          <button
            onClick={() => setScreen('login')}
            className="w-full bg-blue-600 text-white py-2.5 text-sm rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-md"
          >
            J'ai déjà un compte
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
